import React from 'react';
import * as R from 'ramda';
import {
  BooleanInput,
  Create,
  FormDataConsumer,
  FormWithRedirect,
  minValue,
  number,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  Toolbar,
  useQuery,
} from 'react-admin';
import { Box, Typography } from '@material-ui/core';

import { DateInput } from 'resources/common/DatePickers';
import { authServer } from 'auth/keycloak';
import {
  cohortTimezones,
  formats,
  languages,
  tracksB2b,
  b2bProjectTypes,
} from 'data';
import { validateUrl } from '../../utils';
import { Link } from 'react-router-dom';
import { TrendingUp } from '@material-ui/icons';
import AutomaticAttendanceInputCreate from 'resources/common/AutomaticAttendanceInputCreate';
import CohortB2bTimetableInputs from './CohortB2bTimetableInputs';

const fieldsConfig = {
  zoom_url: {
    validate: [
      R.pipe(
        R.defaultTo(''),
        R.trim,
        R.ifElse(
          R.isEmpty,
          R.always(undefined),
          R.ifElse(
            R.allPass([
              R.startsWith('https://ironhack.zoom.us/j/'),
              R.pipe(
                R.replace('https://ironhack.zoom.us/j/', ''),
                R.test(/^\d+$/),
              ),
            ]),
            R.always(undefined),
            R.always(
              'The URL must start with https://ironhack.zoom.us/j/ followed by a meeting ID. For example: https://ironhack.zoom.us/j/1234567890',
            ),
          ),
        ),
      ),
    ],
    parse: R.pipe(R.defaultTo(''), R.trim),
  },
};

const GROUPS = { pm: '8b9f3f55-1147-409e-adb0-7e5f1f6a9d03' };

const createGroupQuery = (pagination, group) => ({
  type: 'getList',
  resource: 'users',
  payload: {
    filter: { group: GROUPS[group], with_groups: false },
    pagination,
  },
});

const validateRequired = [required('Required')];
const validateNumbers = [number(), minValue(0)];

const validateRequiredNumbers = R.concat(validateRequired, validateNumbers);

const CohortB2bCreateForm = (props) => {
  const pms = useQuery(createGroupQuery({ page: 1, perPage: 100 }, 'pm'));

  return (
    <FormWithRedirect
      {...props}
      initialValues={{
        format: 'other',
        has_career_services: true,
        has_prework: true,
        sessions: 1,
        timetable: {
          days: '0111110',
          time: [{ start_time: '09:00', end_time: '18:00' }],
        },
      }}
      render={(formProps) => {
        return (
          <form>
            <Box display="block" p="1.5em">
              <Typography variant="h5" gutterBottom>
                Cohort
              </Typography>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flex={1} mr={{ md: '2em' }}>
                  <SelectInput
                    label="Track"
                    source="track"
                    choices={tracksB2b}
                    validate={validateRequired}
                    variant="standard"
                    onChange={R.path(
                      ['form', 'mutators', 'courseTitleMutator'],
                      formProps,
                    )}
                    fullWidth
                  />
                </Box>
                <Box flex={1} mr={{ md: '2em' }}>
                  <SelectInput
                    label="Format"
                    source="format"
                    choices={formats}
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
                <Box flex={1} mr={{ md: '2em' }}>
                  <SelectInput
                    label="Timezone"
                    source="timezone"
                    choices={cohortTimezones}
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
                <Box flex={1}>
                  <SelectInput
                    label="Language"
                    source="language"
                    choices={languages}
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flexBasis="calc((100% - 6em)/4)" mr={{ md: '2em' }}>
                  <TextInput
                    label="Company"
                    source="company"
                    parse={R.pipe(R.defaultTo(''), R.trim)}
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
                <Box flexBasis="calc((100% - 6em)/4)" mr={{ md: '2em' }}>
                  <SelectInput
                    label="Project Type"
                    source="project_type"
                    choices={b2bProjectTypes}
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
                <Box flexBasis="calc((100% - 6em)/4)">
                  <NumberInput
                    label="Hours per week"
                    source="week_hours"
                    validate={validateRequiredNumbers}
                    defaultValue={0}
                    variant="standard"
                    fullWidth
                  />
                </Box>
              </Box>
              <FormDataConsumer>
                {({ formData }) => (
                  <CohortB2bTimetableInputs format={formData.format} />
                )}
              </FormDataConsumer>
              <Box mb="3em" />
              <Typography variant="h5" gutterBottom>
                Dates
              </Typography>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flexBasis="50%">
                  <DateInput
                    label="Start Date"
                    source="start_date"
                    options={{
                      format: 'Do MMMM YYYY',
                      openTo: 'year',
                      views: ['year', 'month', 'date'],
                    }}
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
                <FormDataConsumer>
                  {({ formData }) =>
                    R.equals('other', formData.format) && (
                      <Box flex={1} ml={{ md: '2em' }}>
                        <DateInput
                          label="End Date"
                          source="end_date"
                          options={{
                            format: 'Do MMMM YYYY',
                            openTo: 'year',
                            views: ['year', 'month', 'date'],
                          }}
                          validate={validateRequired}
                          variant="standard"
                          fullWidth
                        />
                      </Box>
                    )
                  }
                </FormDataConsumer>
              </Box>
              <Box mb="3em" />
              <Typography variant="h5" gutterBottom>
                Staff
              </Typography>
              <Box>
                <SelectInput
                  label="PM"
                  source="staff.pm"
                  format={(pm) =>
                    pm ? `${pm.email}#${pm.first_name}#${pm.last_name}` : ''
                  }
                  parse={R.pipe(
                    R.defaultTo(''),
                    R.split('#'),
                    R.zipObj(['email', 'first_name', 'last_name']),
                  )}
                  choices={R.map(
                    (pm) => {
                      pm.info = `${pm.email}#${pm.first_name}#${pm.last_name}`;
                      return pm;
                    },
                    R.defaultTo([], pms.data),
                  )}
                  variant="standard"
                  optionText={(pm) => `${pm.first_name} ${pm.last_name}`}
                  optionValue="info"
                  fullWidth
                />
              </Box>
              <Box mb="3em">
                <Typography variant="h5" gutterBottom>
                  Custom Cohort Config
                </Typography>
                <Box display="flex" alignItems="baseline">
                  <Box width="120px">
                    <BooleanInput
                      label="Has Prework?"
                      source="has_prework"
                      defaultValue={true}
                      variant="standard"
                      fullWidth
                    />
                  </Box>
                  <Box
                    width="100%"
                    ml="7em"
                    color="grey.600"
                    display="flex"
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      By activating Prework, students get an extra preparation
                      course before the main course. They can access it as soon
                      as they're onboarded, before the Start Date. Only activate
                      it if students must do work before the training starts.
                    </Typography>
                  </Box>
                </Box>
                <Box mt="20px" display="flex" alignItems="baseline">
                  <Box width="120px">
                    <BooleanInput
                      label="Has Careerhack?"
                      source="has_career_services"
                      defaultValue={true}
                      variant="standard"
                      fullWidth
                    />
                  </Box>
                  <Box
                    width="100%"
                    ml="7em"
                    color="grey.600"
                    display="flex"
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      By activating Careerhack, students get the Career Services
                      course. Like the Prework, they can access it as soon as
                      they're onboarded, before the Start Date. Only activate it
                      if the training includes Career Services.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="h5" gutterBottom>
                Cohort specifics
              </Typography>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flexBasis="50%">
                  <TextInput
                    label="Link to FAQs"
                    source="faq_url"
                    validate={validateUrl}
                    variant="standard"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flexBasis="calc((100% - 4em)/2)">
                  <TextInput
                    source="zoom_url"
                    label="Zoom URL"
                    variant="standard"
                    fullWidth
                    {...fieldsConfig.zoom_url}
                  />
                </Box>
              </Box>
              <Box
                width="100%"
                color="grey.600"
                display="flex"
                alignItems="center"
              >
                <Typography variant="body2">
                  By Adding the Zoom URL: Students will be able to access it
                  from the SP. If the course runs in Github, the recordings will
                  be automatically added to a Module called General Info {'>'}{' '}
                  Class Recordings{' '}
                  <Link
                    href="https://ironhack.notion.site/Adding-Zoom-to-a-cohort-265396f8d1fe80a8af86dacbce42e709?source=copy_link"
                    color="primary"
                    target="_blank"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      verticalAlign: 'baseline',
                    }}
                  >
                    <span style={{ lineHeight: '1.43' }}>More info</span>
                    <TrendingUp
                      style={{
                        fontSize: '14px',
                        marginLeft: '4px',
                        marginBottom: '2px',
                      }}
                    />
                  </Link>
                </Typography>
              </Box>
              <Box mt="30px" display="flex" alignItems="baseline">
                <Box width="120px">
                  <FormDataConsumer>
                    {({ formData }) => (
                      <AutomaticAttendanceInputCreate
                        label="Automatic Attendance"
                        source="feature_flags.automatic_attendance_enabled"
                        defaultValue={false}
                        variant="standard"
                        fullWidth
                        record={formData}
                        {...fieldsConfig.feature_flags}
                      />
                    )}
                  </FormDataConsumer>
                </Box>
                <Box
                  width="100%"
                  ml="7em"
                  color="grey.600"
                  display="flex"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    By activating Automatic Attendance, Students will be asked
                    to login with their Ironhack account, we'll review the zoom
                    sessions and add the information to the Attendance tab in CT{' '}
                    <Link
                      href="https://www.notion.so/ironhack/CT-Automatic-Attendance-265396f8d1fe809e8ce2d0d6ef1d22e1?source=copy_link"
                      color="primary"
                      target="_blank"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        verticalAlign: 'baseline',
                      }}
                    >
                      <span style={{ lineHeight: '1.43' }}>More info</span>
                      <TrendingUp
                        style={{
                          fontSize: '14px',
                          marginLeft: '4px',
                          marginBottom: '2px',
                        }}
                      />
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Toolbar>
              {authServer.isRoles(['pm', 'b2b']) && (
                <Box display="flex" width="100%">
                  <Box flex={2}>
                    <SaveButton
                      record={formProps.record}
                      basePath={formProps.basePath}
                      undoable={true}
                      invalid={formProps.invalid}
                      saving={formProps.saving}
                      resource="cohorts"
                      handleSubmitWithRedirect={
                        formProps.handleSubmitWithRedirect
                      }
                    />
                  </Box>
                </Box>
              )}
            </Toolbar>
          </form>
        );
      }}
    />
  );
};

const CohortB2bCreate = ({ classes, ...props }) => {
  return (
    <Create actions={null} undoable={false} {...props}>
      <CohortB2bCreateForm redirect="list" />
    </Create>
  );
};

export default CohortB2bCreate;
